/**
 * Derives the season audit state shown in the planner's "Statut audit" column.
 *
 * Structural input types (rather than StadiumAuditPlannerData) keep this
 * testable with small literals, matching utils/stadiumAuditFocus.ts.
 */

export type AuditRowState =
  | 'audited'
  | 'reaudit_required'
  | 'submitted'
  | 'rejected'
  | 'scheduled'
  | 'none';

export interface AuditRowOrderRef {
  orderNumber: string;
  createdAt: string;
  emailedAt: string | null;
  emailCount: number;
}

export interface AuditRowAssignment {
  id: string;
  official_id: string | null;
  /** mission_assignment_status — the official's own reply, set from the mobile app. */
  status?: string | null;
  order?: AuditRowOrderRef | null;
}

export interface AuditRowMission {
  id: string;
  target_id?: string | null;
  mission_status: string;
  start_at: string | null;
  audit?: {
    status: string;
    scheduled_at: string | null;
    completed_at: string | null;
    /** Posé par la ligue à la validation : « non homologué — contre-visite requise ». */
    requires_followup?: boolean;
    cycle_index?: number;
    /** Présent sur la contre-visite, pointe vers le premier audit du cycle. */
    previous_audit_id?: string | null;
  } | null;
  mission_assignments?: AuditRowAssignment[];
}

export interface AuditRowOrderSummary {
  total: number;
  emailed: number;
  lastOrderNumber: string | null;
  lastEmailedAt: string | null;
  lastCreatedAt: string | null;
}

/** How the assigned officials replied to the convocation, for the league's at-a-glance
 *  badges. `total` counts only assignments still in play (cancelled ones are excluded),
 *  so accepted + declined + awaiting === total always holds. */
export interface AuditResponseTally {
  accepted: number;
  declined: number;
  awaiting: number;
  total: number;
}

export const EMPTY_AUDIT_RESPONSES: AuditResponseTally = {
  accepted: 0,
  declined: 0,
  awaiting: 0,
  total: 0,
};

export interface AuditRowStatus {
  state: AuditRowState;
  date: string | null;
  missionId: string | null;
  assignmentIds: string[];
  officialsCount: number;
  order: AuditRowOrderSummary | null;
  responses: AuditResponseTally;
  /** Le cycle courant est une contre-visite (son audit porte previous_audit_id). */
  isSecondVisit: boolean;
  /** Numéro de la visite affichée (1, 2 = contre-visite, 3+ = supplémentaire). */
  visitNumber: number;
}

/** "Nothing planned" row — shared so callers don't re-declare (and drift from) the shape. */
export const EMPTY_AUDIT_ROW_STATUS: AuditRowStatus = {
  state: 'none',
  date: null,
  missionId: null,
  assignmentIds: [],
  officialsCount: 0,
  order: null,
  responses: EMPTY_AUDIT_RESPONSES,
  isSecondVisit: false,
  visitNumber: 1,
};

/** Source de vérité unique du verrou de cycle, partagée par le panneau et les tests.
 *  Le miroir serveur est assertCycleUnlocked() dans supabase/functions/stadium-audit-planner
 *  (il couvre assign, assignment.update, assignment.remove et la suppression d'audit)
 *  — les deux doivent changer ensemble. Polarité inversée : le helper serveur renvoie
 *  true quand le cycle est DÉverrouillé et se base sur le statut BD `completed`, alors
 *  que celui-ci renvoie true quand il est verrouillé et se base sur l'état de ligne
 *  client `audited`. C'est la même condition dans les deux vocabulaires.
 *
 *  S'arrête à `audited` (audit validé). `submitted` reste ouvert : la ligue peut encore
 *  corriger l'équipe ou la date avant de valider. `reaudit_required` n'est PAS verrouillé :
 *  le cycle précédent est clos, mais la ligue doit précisément pouvoir planifier la
 *  contre-visite (assign crée alors une nouvelle mission — le serveur ne re-touche jamais
 *  le cycle validé). */
export function isAuditCycleLocked(state: AuditRowState): boolean {
  return state === 'audited';
}

const EMPTY = EMPTY_AUDIT_ROW_STATUS;

// Higher wins AMONG OPEN CYCLES (see deriveAuditRowStatus: a closed/validated cycle only
// shows when no open cycle exists for the stadium).
const RANK: Record<AuditRowState, number> = {
  audited: 5,
  reaudit_required: 4,
  submitted: 3,
  rejected: 2, // sent back to the auditor: still an active cycle, not "nothing planned"
  scheduled: 1,
  none: 0,
};

const IGNORED_MISSION_STATUSES = new Set(['archived', 'cancelled', 'deleted']);

function stateOf(mission: AuditRowMission): AuditRowState {
  const status = mission.audit?.status ?? mission.mission_status;
  if (status === 'completed') return 'audited';
  if (status === 'submitted') return 'submitted';
  if (status === 'rejected') return 'rejected';
  if (status === 'scheduled' || status === 'in_progress') return 'scheduled';
  return 'none';
}

function dateOf(mission: AuditRowMission): string | null {
  return mission.audit?.completed_at ?? mission.audit?.scheduled_at ?? mission.start_at ?? null;
}

function summariseOrders(assignments: AuditRowAssignment[]): AuditRowOrderSummary | null {
  const orders = assignments.map((a) => a.order).filter((o): o is AuditRowOrderRef => !!o);
  if (orders.length === 0) return null;

  const latestBy = (pick: (o: AuditRowOrderRef) => string | null): AuditRowOrderRef | null =>
    orders.reduce<AuditRowOrderRef | null>((best, o) => {
      const v = pick(o);
      if (!v) return best;
      const bestV = best ? pick(best) : null;
      return !bestV || v > bestV ? o : best;
    }, null);

  const newestCreated = latestBy((o) => o.createdAt);
  const newestEmailed = latestBy((o) => o.emailedAt);

  return {
    total: orders.length,
    emailed: orders.filter((o) => !!o.emailedAt).length,
    lastOrderNumber: newestCreated?.orderNumber ?? null,
    lastEmailedAt: newestEmailed?.emailedAt ?? null,
    lastCreatedAt: newestCreated?.createdAt ?? null,
  };
}

// mission_assignment_status values that mean the official answered.
const ACCEPTED_STATUSES = new Set(['accepted', 'completed']);
const DECLINED_STATUSES = new Set(['declined']);
/** A withdrawn convocation is no longer waiting on anybody — it leaves the tally.
 *
 *  Canonical set, shared with every money path and every "who did this audit" display.
 *  A `cancelled` mission_assignment row is KEPT in the database for traceability (it is
 *  what audit.correct_team writes when the league removes an official from an already
 *  validated audit), but it must never be counted again: no payment amount, no total,
 *  no officials list, no workload counter, no "already assigned" exclusion. Import this
 *  set (or `isCountedAssignment`) rather than re-typing `!== 'cancelled'` inline — the
 *  branch's whole safety premise is that there is exactly one definition. */
export const UNCOUNTED_ASSIGNMENT_STATUSES: ReadonlySet<string> = new Set(['cancelled']);

/** True when an assignment row still counts (i.e. it was not withdrawn). */
export function isCountedAssignment(
  assignment: { status?: string | null } | null | undefined,
): boolean {
  if (!assignment) return false;
  return !UNCOUNTED_ASSIGNMENT_STATUSES.has(assignment.status ?? '');
}

function tallyResponses(assignments: AuditRowAssignment[]): AuditResponseTally {
  let accepted = 0;
  let declined = 0;
  let awaiting = 0;
  for (const a of assignments) {
    const status = a.status ?? '';
    if (ACCEPTED_STATUSES.has(status)) accepted += 1;
    else if (DECLINED_STATUSES.has(status)) declined += 1;
    // pending / notified / acknowledged / unset: convoked but silent so far.
    else awaiting += 1;
  }
  return { accepted, declined, awaiting, total: accepted + declined + awaiting };
}

/** Roll per-stadium tallies up to a group (or selection) header. */
export function sumAuditResponses(
  tallies: Array<AuditResponseTally | null | undefined>,
): AuditResponseTally {
  return tallies.reduce<AuditResponseTally>(
    (acc, t) =>
      t
        ? {
            accepted: acc.accepted + t.accepted,
            declined: acc.declined + t.declined,
            awaiting: acc.awaiting + t.awaiting,
            total: acc.total + t.total,
          }
        : acc,
    { ...EMPTY_AUDIT_RESPONSES },
  );
}

export function deriveAuditRowStatus(
  missions: AuditRowMission[] | undefined,
  stadiumId: string,
): AuditRowStatus {
  if (!missions?.length || !stadiumId) return EMPTY;

  const candidates = missions.filter(
    (m) => m.target_id === stadiumId && !IGNORED_MISSION_STATUSES.has(m.mission_status),
  );
  if (candidates.length === 0) return EMPTY;

  // Cycle courant plutôt que « meilleure mission de tous les temps » : un stade peut
  // porter un audit validé (cycle clos) ET une contre-visite planifiée. Le cycle ouvert
  // (scheduled/rejected/submitted) l'emporte toujours sur un cycle clos — sinon la
  // contre-visite fraîchement planifiée resterait invisible derrière « Audité ».
  let best: AuditRowMission | null = null;
  let bestState: AuditRowState = 'none';
  let bestOpen = false;

  for (const m of candidates) {
    const state = stateOf(m);
    if (state === 'none') continue;
    const open = state !== 'audited';
    const better =
      !best ||
      (open && !bestOpen) ||
      (open === bestOpen &&
        (RANK[state] > RANK[bestState] ||
          (RANK[state] === RANK[bestState] && (m.start_at ?? '') > (best.start_at ?? ''))));
    if (better) {
      best = m;
      bestState = state;
      bestOpen = open;
    }
  }

  if (!best) return EMPTY;

  // Cycle clos non homologué, sans contre-visite encore planifiée : état dédié pour que
  // la ligue voie l'action attendue (et que le verrou de cycle ne s'applique pas).
  if (bestState === 'audited' && best.audit?.requires_followup) {
    bestState = 'reaudit_required';
  }

  const assignments = best.mission_assignments ?? [];
  // Cancelled convocations drop out everywhere, so officialsCount stays equal to
  // responses.total and a withdrawn official is never counted as still awaiting.
  const assigned = assignments.filter(
    (a) => !!a.official_id && !UNCOUNTED_ASSIGNMENT_STATUSES.has(a.status ?? ''),
  );

  return {
    state: bestState,
    date: dateOf(best),
    missionId: best.id,
    assignmentIds: assigned.map((a) => a.id),
    officialsCount: assigned.length,
    order: summariseOrders(assigned),
    responses: tallyResponses(assigned),
    isSecondVisit: !!best.audit?.previous_audit_id,
    visitNumber: best.audit?.cycle_index ?? (best.audit?.previous_audit_id ? 2 : 1),
  };
}
